<script lang="ts" module>
	export { floatingAside };

	let firstPageRendered = false;

	// svelte wants to keep its state transactional, either changing it all or not at all.
	// when our router changes between pages, that also counts, so we never get the loading indicator.
	//
	// what we can do instead is to track this state outside svelte
	let currentLoadingDisplay: HTMLElement | undefined;
	const loadingDisplay = (element: HTMLElement) => {
		currentLoadingDisplay = element;

		return () => {
			currentLoadingDisplay = undefined;
		};
	};

	addEventListener("data:loading", () => {
		currentLoadingDisplay?.classList.add("_loading");
	});

	addEventListener("data:done", () => {
		currentLoadingDisplay?.classList.remove("_loading");
	});

	addEventListener("router:loading", () => {
		currentLoadingDisplay?.classList.add("_navigating");
	});

	addEventListener("router:done", () => {
		currentLoadingDisplay?.classList.remove("_navigating");
	});
</script>

<script lang="ts">
	import type { Snippet } from "svelte";
	import type { RawSvg } from "vite-plugin-magical-svg";

	import Icon from "#/lib/Icon.svelte";
	import { flow } from "#/lib/flow.module.css";
	import { container } from "#/lib/panel.module.css";

	interface Props {
		title: string | Snippet;
		browserTitle?: string; /* should be non-null if title snippet is used */
		icon: RawSvg;
		children: Snippet<[]>;
		aside?: Snippet<[]>;
	}

	const { title, browserTitle, icon, children, aside }: Props = $props();

	if (!firstPageRendered) {
		$effect(() => {
			firstPageRendered = true;
			document.querySelector("#splash")?.remove();
		});
	}
</script>

{#snippet floatingAside(children: Snippet)}
	<div class="_floating {flow}">{@render children()}</div>
{/snippet}

<svelte:head>
	<title>{browserTitle ?? title}</title>
</svelte:head>

<header>
	<div class={["_floating", container]} {@attach loadingDisplay}>
		<Icon class={__styles.icon} {icon} />

		<h1>
			<!-- @wc-ignore -->
			{#if typeof title == "string"}
				{title}
			{:else}
				{@render title()}
			{/if}
		</h1>
	</div>
</header>

<aside>
	{@render aside?.()}
</aside>

<main>
	{@render children()}
</main>

<style>
	._floating {
		position: fixed;
		z-index: 1000;
	}

	header {
		container-type: inline-size;
		grid-area: header;

		> ._floating {
			contain: paint;
			display: flex;
			gap: var(--s-2xs);
			align-items: center;

			inline-size: 100cqi;
			block-size: var(--_header-height);
			padding-block: 0;
			padding-inline: calc(var(--_panel-padding) * 2);
			border-start-start-radius: 0;
			border-start-end-radius: 0;

			box-shadow:
				var(--shadow-panel-container),
				0 0 20px #00000090;

			> :local(.icon) {
				/* TODO: just adjust the padding instead dumbass */
				margin-inline-start: calc(var(--_panel-padding) * -1);
			}
		}
	}

	h1 {
		display: flex;
		gap: 0.25ch;
		font-weight: var(--w-bold);
		color: var(--c-text-bold);
	}

	@keyframes pulse {
		from {
			opacity: 0;
		}

		to {
			opacity: 0.25;
		}
	}

	@keyframes throb {
		0% {
			translate: 0 0;
		}

		100% {
			translate: calc((100cqi - 100%) * var(--rtl-mult)) 0;
		}
	}

	:is(._loading, ._navigating)::before {
		pointer-events: none;
		content: "";

		position: absolute;

		display: block;

		margin-inline-start: calc(var(--_panel-padding) * -2);
		/* stylelint-disable-next-line defensive-css/require-prefers-reduced-motion */
		animation: 1s var(--ease-in-out) alternate infinite;

		@media (prefers-reduced-motion: no-preference) {
			/* throbber */

			inline-size: 10px;
			block-size: 1%;

			background-color: #fff3;
			box-shadow: 0 0 20px 20px #fff3;

			animation-name: throb;
		}

		@media (prefers-reduced-motion: reduce) {
			/* pulsing */

			inline-size: 100%;
			block-size: 100%;
			border-radius: var(--_panel-container-radius);
			border-start-start-radius: 0;
			border-start-end-radius: 0;

			background-color: white;

			animation-name: pulse;
		}
	}

	main {
		grid-area: main;
	}

	aside {
		container-type: inline-size;
		grid-area: aside;
		max-inline-size: 70%;
		block-size: fit-content;

		> :global(._floating) {
			inline-size: 100cqi;
		}
	}
</style>
